// MirrorDetailDialog.js
// Detail modal for Mirror Schedule - dimensions and picture upload

import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NumericInput } from '@/components/NumericInput';
import { Upload, ClipboardPaste, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Inner form component that resets when key changes
const MirrorDetailForm = ({ 
  mirror,
  onSave,
  onUploadPicture,
  onDeletePicture,
  uploading,
  onClose
}) => {
  const [formData, setFormData] = useState({
    mirrorWidth_in: mirror?.mirrorWidth_in || null,
    mirrorHeight_in: mirror?.mirrorHeight_in || null
  });
  const fileInputRef = useRef(null);

  const handleSave = () => {
    onSave(formData);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onUploadPicture(file);
    }
  };

  const handlePaste = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        for (const type of item.types) {
          if (type.startsWith('image/')) {
            const blob = await item.getType(type);
            const file = new File([blob], `paste-${Date.now()}.png`, { type });
            onUploadPicture(file);
            return;
          }
        }
      }
      toast.error('No image found in clipboard');
    } catch (error) {
      toast.error('Failed to paste from clipboard');
    }
  };

  const pictureUrl = mirror?.pictureCloudinary?.urls?.medium || mirror?.pictureUrl;

  return (
    <div className="space-y-6 mt-4">
      {/* Mirror Dimensions */}
      <div className="space-y-4">
        <h4 className="font-semibold text-sm text-gray-700 border-b pb-2">Mirror Dimensions</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Mirror Width (in)</Label>
            <NumericInput
              allowFraction
              placeholder="e.g., 36"
              value={formData.mirrorWidth_in || ''}
              onChange={(v) => setFormData({
                ...formData,
                mirrorWidth_in: v
              })}
            />
          </div>
          <div className="space-y-2">
            <Label>Mirror Height (in)</Label>
            <NumericInput
              allowFraction
              placeholder="e.g., 48"
              value={formData.mirrorHeight_in || ''}
              onChange={(v) => setFormData({
                ...formData,
                mirrorHeight_in: v
              })}
            />
          </div>
        </div>
      </div>

      {/* Schedule Image */}
      <div className="space-y-4">
        <h4 className="font-semibold text-sm text-gray-700 border-b pb-2">Schedule Image</h4>
        
        {pictureUrl ? (
          <div className="relative">
            <img 
              src={pictureUrl} 
              alt={mirror?.label} 
              className="w-full h-48 object-contain rounded-lg border bg-slate-50"
            />
            <Button
              variant="destructive"
              size="sm"
              className="absolute top-2 right-2"
              onClick={() => onDeletePicture(mirror.id)}
              disabled={uploading}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
            <p className="text-slate-500 text-sm mb-4">No image uploaded yet</p>
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                onClick={handlePaste}
                disabled={uploading}
              >
                <ClipboardPaste className="h-4 w-4 mr-2" />
                Paste
              </Button>
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Upload
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
        )}

        {pictureUrl && (
          <div className="flex justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePaste}
              disabled={uploading}
            >
              <ClipboardPaste className="h-4 w-4 mr-2" />
              Paste
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Replace Image
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave}>
          Save Changes
        </Button>
      </div>
    </div>
  );
};

const MirrorDetailDialog = ({ 
  open, 
  onOpenChange, 
  mirror,
  onSave,
  onUploadPicture,
  onDeletePicture,
  uploading = false
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Mirror Details: {mirror?.label}</DialogTitle>
          <DialogDescription>
            Update mirror dimensions and upload schedule image
          </DialogDescription>
        </DialogHeader>

        {mirror && (
          <MirrorDetailForm
            key={mirror.id}
            mirror={mirror}
            onSave={onSave}
            onUploadPicture={onUploadPicture}
            onDeletePicture={onDeletePicture}
            uploading={uploading}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MirrorDetailDialog;
